import { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/api';
import { OFFER_BENEFITS } from './offersCopy';

/**
 * Bloc Super Admin — Offre Communication (Inclus / Plus).
 * Distinct des permissions d'accès au module.
 * Premium technique non commercialisé : non proposé ici.
 */
export default function CommunicationOfferAdminPanel({
  commercantId,
  currentTier = 'INCLUS',
  usage = null,
  onUpdated,
}) {
  const [draft, setDraft] = useState(() => normalizeUiTier(currentTier));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setDraft(normalizeUiTier(currentTier));
  }, [currentTier, commercantId]);

  const savedTier = normalizeUiTier(currentTier);
  const dirty = draft !== savedTier;

  const downgradeWarning = useMemo(
    () => buildDowngradeWarning({ draft, savedTier, usage }),
    [draft, savedTier, usage]
  );

  const save = async () => {
    if (!commercantId || !dirty || saving) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const res = await apiService.patchGrowthTier({
        commercantId,
        growthTier: draft,
      });
      const next = normalizeUiTier(res?.commercant?.growthTier || res?.entitlements?.tier || draft);
      setDraft(next);
      setSuccess(
        `Offre Communication enregistrée : ${OFFER_BENEFITS[next]?.label || next}. Les capacités sont mises à jour immédiatement. L’historique des campagnes reste visible.`
      );
      if (typeof onUpdated === 'function') {
        await onUpdated(res);
      }
    } catch (err) {
      setError(err?.message || 'Impossible de mettre à jour l’offre Communication.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Offre Communication</p>
        <p className="mt-1 text-xs text-gray-600 max-w-3xl">
          Capacités d’abonnement du commerce (Inclus ou Plus). Ce n’est pas une permission : qui
          peut ouvrir le module se gère dans Permissions.
        </p>
        <p className="mt-2 text-sm text-gray-800">
          Offre actuelle :{' '}
          <span className="inline-flex items-center rounded-md bg-white border border-gray-200 px-2 py-0.5 font-semibold">
            {OFFER_BENEFITS[savedTier]?.label || 'Inclus'}
          </span>
          {String(currentTier || '').toUpperCase() === 'PREMIUM' && (
            <span className="ml-2 text-xs text-gray-500">
              (technique Premium → affichée Plus côté boutique)
            </span>
          )}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {['INCLUS', 'PLUS'].map((key) => {
          const offer = OFFER_BENEFITS[key];
          const selected = draft === key;
          const isSaved = savedTier === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                setDraft(key);
                setSuccess('');
                setError('');
              }}
              className={`rounded-md border px-3 py-2 text-left min-w-[10rem] ${
                selected
                  ? 'border-primary-500 bg-white ring-1 ring-primary-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-900">{offer.label}</span>
                {isSaved && (
                  <span className="text-[10px] uppercase tracking-wide text-primary-700">Actuelle</span>
                )}
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">{offer.tagline}</span>
            </button>
          );
        })}
      </div>

      {downgradeWarning && (
        <p className="text-sm text-amber-900 bg-amber-100/80 border border-amber-200 rounded-md px-3 py-2">
          {downgradeWarning}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      {success && (
        <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
          {success}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <button
          type="button"
          className="btn-primary text-sm disabled:opacity-50"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
}

function normalizeUiTier(tier) {
  const t = String(tier || 'INCLUS').toUpperCase();
  if (t === 'PLUS' || t === 'PREMIUM') return 'PLUS';
  return 'INCLUS';
}

/** Caps commerciaux visibles (alignés backend growthConfig). */
const UI_CAPS = {
  INCLUS: {
    emailCampaignsPerMonth: 3,
    inAppCampaignsPerMonth: 5,
    maxEmailRecipientsPerCampaign: 200,
  },
  PLUS: {
    emailCampaignsPerMonth: 6,
    inAppCampaignsPerMonth: 10,
    maxEmailRecipientsPerCampaign: 2000,
  },
};

function buildDowngradeWarning({ draft, savedTier, usage }) {
  if (draft !== 'INCLUS' || savedTier === 'INCLUS') return null;
  const emailUsed = Number(usage?.emailCampaignCount ?? 0);
  const inAppUsed = Number(usage?.inAppCampaignCount ?? 0);
  const emailMax = UI_CAPS.INCLUS.emailCampaignsPerMonth;
  const inAppMax = UI_CAPS.INCLUS.inAppCampaignsPerMonth;
  const emailBlocked = emailUsed >= emailMax;
  const inAppBlocked = inAppUsed >= inAppMax;

  if (emailBlocked && inAppBlocked) {
    return `Attention : ce commerce a déjà atteint les plafonds Inclus ce mois-ci (${emailMax} email, ${inAppMax} In-App). Aucun nouvel envoi ne sera possible jusqu’au mois suivant. L’historique reste visible.`;
  }
  if (emailBlocked) {
    return `Attention : ce commerce a déjà utilisé ${emailUsed} campagne${emailUsed > 1 ? 's' : ''} email ce mois-ci (max Inclus : ${emailMax}). Les envois email seront bloqués jusqu’au mois suivant ; l’In-App reste soumis à son propre quota (${inAppMax}/mois).`;
  }
  if (inAppBlocked) {
    return `Attention : ce commerce a déjà utilisé ${inAppUsed} campagne${inAppUsed > 1 ? 's' : ''} In-App ce mois-ci (max Inclus : ${inAppMax}). Les notifications In-App seront bloquées jusqu’au mois suivant ; l’email reste soumis à son propre quota (${emailMax}/mois).`;
  }
  return `Passage de Plus à Inclus : les capacités seront réduites immédiatement (${emailMax} email et ${inAppMax} In-App / mois, ${UI_CAPS.INCLUS.maxEmailRecipientsPerCampaign} destinataires max par email). L’historique des campagnes reste visible.`;
}
